import React, { useContext } from "react";
import {
  KivuEnvConfigContext,
  PhoneCountryCodeMap,
  useKivunovaTranslation,
  useToggleVisibility
} from "@kivunova/kivufrontendcommon";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { motion } from "framer-motion";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { RegistrationData } from "./types/registrationTypes";
import {
  registrationInputSx,
  registrationSelectSx,
  registrationFieldMotion
} from "./registrationFormStyles";
import PasswordVisibilityAdornment from "../common/PasswordVisibilityAdornment/PasswordVisibilityAdornment";

interface AdminInfoFormProps {
  control: Control<RegistrationData>;
  errors: FieldErrors<RegistrationData>;
}

const AdminInfoForm: React.FC<AdminInfoFormProps> = ({ control, errors }) => {
  const { t } = useKivunovaTranslation();
  const { countryCode } = useContext(KivuEnvConfigContext);
  const { isVisible: isPasswordVisible, toggle: togglePasswordVisibility } =
    useToggleVisibility(false);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
      component={motion.div}
      initial="initial"
      animate="animate"
      variants={{
        initial: {},
        animate: {
          transition: { staggerChildren: 0.05, delayChildren: 0.05 }
        }
      }}
    >
      <Box component={motion.div} variants={registrationFieldMotion}>
        <Controller
          name="admin.firstName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t("first-name", "First name")}
              fullWidth
              error={!!errors?.admin?.firstName}
              helperText={errors?.admin?.firstName?.message}
              sx={registrationInputSx}
              inputProps={{ "data-testid": "registration-firstName" }}
            />
          )}
        />
      </Box>
      <Box component={motion.div} variants={registrationFieldMotion}>
        <Controller
          name="admin.lastName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t("last-name", "Last name")}
              fullWidth
              error={!!errors?.admin?.lastName}
              helperText={errors?.admin?.lastName?.message}
              sx={registrationInputSx}
              inputProps={{ "data-testid": "registration-lastName" }}
            />
          )}
        />
      </Box>
      <Box component={motion.div} variants={registrationFieldMotion}>
        <Controller
          name="admin.email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t("email", "Email")}
              fullWidth
              error={!!errors?.admin?.email}
              helperText={errors?.admin?.email?.message}
              sx={registrationInputSx}
              inputProps={{ "data-testid": "registration-email" }}
            />
          )}
        />
      </Box>
      <Box component={motion.div} variants={registrationFieldMotion}>
        <Controller
          name="admin.phoneCountryCode"
          control={control}
          defaultValue={
            PhoneCountryCodeMap[countryCode]?.countryCode ??
            Object.keys(PhoneCountryCodeMap)[0]
          }
          render={({ field }) => (
            <FormControl fullWidth error={!!errors?.admin?.phoneCountryCode}>
              <InputLabel shrink id="phone-country-code-select-label">
                {t("phone-country-code-label-text", "Country Code")}
              </InputLabel>
              <Select
                {...field}
                labelId="phone-country-code-select-label"
                id="registration-phone-country-code"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                displayEmpty
                label={t("phone-country-code-label-text", "Country Code")}
                sx={registrationSelectSx}
              >
                {Object.keys(PhoneCountryCodeMap).map((country) => (
                  <MenuItem
                    key={PhoneCountryCodeMap[country].code}
                    value={country}
                  >
                    {`${PhoneCountryCodeMap[country].flag} ${PhoneCountryCodeMap[country].code}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Box>
      <Box component={motion.div} variants={registrationFieldMotion}>
        <Controller
          name="admin.phone"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t("phone", "Phone")}
              fullWidth
              error={!!errors?.admin?.phone}
              helperText={errors?.admin?.phone?.message}
              placeholder="782484464"
              sx={registrationInputSx}
              inputProps={{ "data-testid": "registration-phone" }}
            />
          )}
        />
      </Box>
      <Box component={motion.div} variants={registrationFieldMotion}>
        <Controller
          name="admin.password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t("password", "Password")}
              type={isPasswordVisible ? "text" : "password"}
              fullWidth
              error={!!errors?.admin?.password}
              helperText={errors?.admin?.password?.message}
              sx={registrationInputSx}
              inputProps={{ "data-testid": "registration-password" }}
              InputProps={{
                endAdornment: (
                  <PasswordVisibilityAdornment
                    isVisible={isPasswordVisible}
                    onToggle={togglePasswordVisibility}
                  />
                )
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default AdminInfoForm;
